import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { Modal, List, Tag, Button, message } from 'antd';
import {
  DesktopOutlined,
  MobileOutlined,
  TabletOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import alert from '@components/alert';
import { getOtherSessions } from '@service/activeSessionsService';
import actions from '@flux/action';
import './ActiveSessionsModal.scss';

const DEVICE_ICONS = {
  desktop: DesktopOutlined,
  mobile: MobileOutlined,
  tablet: TabletOutlined,
};

function getDeviceIcon(deviceType) {
  const Icon = DEVICE_ICONS[deviceType] || DesktopOutlined;
  return <Icon className="session-device-icon" />;
}

function formatDeviceLabel(session) {
  return session.deviceLabel || 'Unknown device';
}

function getSessionKey(session) {
  return session.sub || session.sessionId;
}

const ActiveSessionsModal = forwardRef(({ onDismiss }, ref) => {
  const dispatch = useDispatch();
  const { activeSessions, currentSessionId, activeCount } = useSelector(
    (state) => state.login,
  );
  const [visible, setVisible] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const otherSessions = getOtherSessions(activeSessions, currentSessionId);

  useImperativeHandle(ref, () => ({
    show: () => setVisible(true),
    hide: () => setVisible(false),
  }));

  const handleDismiss = useCallback(() => {
    dispatch(actions.dismissActiveSessionsModal());
    setVisible(false);
    onDismiss?.();
  }, [dispatch, onDismiss]);

  const handleRevokeSession = (session) => {
    const sessionKey = getSessionKey(session);
    alert.confirm({
      title: 'Log out this device?',
      content: `This will end the session on ${formatDeviceLabel(session)}.`,
      okText: 'Log out',
      cancelText: 'Cancel',
      onOk: async () => {
        setRevokingId(sessionKey);
        try {
          await dispatch(actions.revokeSession(sessionKey));
          message.success('Session logged out successfully.');
        } catch {
          message.error('Failed to log out session. Please try again.');
        } finally {
          setRevokingId(null);
        }
      },
    });
  };

  const handleRevokeAll = () => {
    alert.confirm({
      title: 'Log out all other devices?',
      content: `This will end ${otherSessions.length} other active session${
        otherSessions.length === 1 ? '' : 's'
      }.`,
      okText: 'Log out all',
      cancelText: 'Cancel',
      onOk: async () => {
        setRevokingAll(true);
        try {
          await dispatch(actions.revokeAllOtherSessions());
          message.success('All other sessions logged out successfully.');
          handleDismiss();
        } catch {
          message.error('Failed to log out sessions. Please try again.');
        } finally {
          setRevokingAll(false);
        }
      },
    });
  };

  const sessionCountLabel =
    activeCount > 1
      ? `You have ${activeCount} active sessions.`
      : 'You are signed in on multiple devices.';

  return (
    <Modal
      title={
        <div className="active-sessions-modal-title">
          <GlobalOutlined className="sessions-icon" />
          <span>Active Sessions</span>
        </div>
      }
      visible={visible}
      onCancel={handleDismiss}
      footer={
        <div className="active-sessions-modal-footer">
          {otherSessions.length > 0 && (
            <Button
              danger
              loading={revokingAll}
              onClick={handleRevokeAll}
              className="revoke-all-btn"
            >
              Log out all other devices
            </Button>
          )}
          <Button type="primary" onClick={handleDismiss} className="continue-btn">
            Continue
          </Button>
        </div>
      }
      centered
      closable
      maskClosable
      destroyOnClose={false}
      wrapClassName="active-sessions-modal-wrap"
      className="active-sessions-modal"
      width={560}
    >
      <div className="active-sessions-modal-content">
        <p className="active-sessions-description">
          {sessionCountLabel} Review your active sessions below and log out any
          you do not recognize.
        </p>

        <List
          className="active-sessions-list"
          dataSource={activeSessions}
          locale={{ emptyText: 'No active sessions found.' }}
          renderItem={(session) => {
            const sessionKey = getSessionKey(session);
            const isCurrent =
              session.isCurrent || sessionKey === currentSessionId;

            return (
              <List.Item
                className={`session-item ${isCurrent ? 'session-item-current' : ''}`}
                actions={
                  isCurrent
                    ? []
                    : [
                        <Button
                          key="logout"
                          type="link"
                          danger
                          loading={revokingId === sessionKey}
                          onClick={() => handleRevokeSession(session)}
                        >
                          Log out
                        </Button>,
                      ]
                }
              >
                <List.Item.Meta
                  avatar={getDeviceIcon(session.deviceType)}
                  title={
                    <div className="session-title-row">
                      <span className="session-device-label">
                        {formatDeviceLabel(session)}
                      </span>
                      {isCurrent && (
                        <Tag color="orange" className="current-device-tag">
                          This device
                        </Tag>
                      )}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </div>
    </Modal>
  );
});

ActiveSessionsModal.displayName = 'ActiveSessionsModal';

export default ActiveSessionsModal;
